import { ConversationDraftScope } from '@provision/conversation-types';
import { stableStringify } from '@provision/formatting-utils';
import { parseJsonSafely } from 'src/utils/objects';

export interface SessionDraft {
  content: string;
  ts: number;
  /**
   * high-water timestamp of the content last confirmed persisted to the server.
   * Lets us tell an un-persisted local edit (which must be preserved) apart from a
   * draft that was removed on the server elsewhere (which must not be resurrected).
   * `0` means the current content has never been confirmed on the server.
   */
  syncedTs: number;
}

export type DraftScope = ConversationDraftScope;

export function makeDraftKey(
  conversationId: string,
  isPrivate: boolean,
  scope: DraftScope = ConversationDraftScope.Unscheduled,
): string {
  return `draft:${conversationId}:${isPrivate ? 'private' : 'public'}:${scope}`;
}

export function readSessionDraft(key: string): SessionDraft {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return { content: '', ts: 0, syncedTs: 0 };
    }
    const parsed =
      parseJsonSafely<Partial<SessionDraft> & { message?: unknown }>(raw) ??
      ({} as Partial<SessionDraft> & { message?: unknown });
    const contentCandidate =
      typeof parsed.message === 'string' ? parsed.message : parsed.content;
    const content = typeof contentCandidate === 'string' ? contentCandidate : '';
    const tsValue = typeof parsed.ts === 'number' ? parsed.ts : Number(parsed.ts);
    const ts = Number.isFinite(tsValue) ? Number(tsValue) : 0;
    const syncedValue =
      typeof parsed.syncedTs === 'number' ? parsed.syncedTs : Number(parsed.syncedTs);
    const syncedTs = Number.isFinite(syncedValue) ? syncedValue : 0;
    return { content, ts, syncedTs };
  } catch {
    return { content: '', ts: 0, syncedTs: 0 };
  }
}

export function writeSessionDraft(key: string, content: string): number {
  const ts = Date.now();
  try {
    // preserve the sync high-water mark across edits
    const prev = readSessionDraft(key);
    sessionStorage.setItem(
      key,
      stableStringify({ content: content ?? '', ts, syncedTs: prev.syncedTs }),
    );
  } catch {
    /* ignore quota/serialization errors */
  }
  return ts;
}

export function markSessionDraftSynced(key: string, syncedContent: string): void {
  try {
    const prev = readSessionDraft(key);
    if (prev.content !== (syncedContent ?? '')) {
      return;
    }
    sessionStorage.setItem(
      key,
      stableStringify({ content: prev.content, ts: prev.ts, syncedTs: prev.ts }),
    );
  } catch (error) {
    console.error(error);
  }
}

export function resolveDraftContent(params: {
  serverContent: string;
  serverTs: number;
  serverHasDraft: boolean;
  serverLoaded: boolean;
  session: SessionDraft;
}): string {
  const { serverContent, serverTs, serverHasDraft, serverLoaded, session } = params;

  if (serverHasDraft) {
    return serverTs >= session.ts ? serverContent : session.content;
  }

  if (!serverLoaded) {
    return session.content;
  }

  return session.ts > session.syncedTs ? session.content : '';
}

export function clearLocalDraftForConversation(conversationId: string): void {
  try {
    [ConversationDraftScope.Unscheduled, ConversationDraftScope.Scheduled].forEach((scope) => {
      sessionStorage.removeItem(makeDraftKey(conversationId, true, scope));
      sessionStorage.removeItem(makeDraftKey(conversationId, false, scope));
    });
  } catch {
    /* ignore */
  }
}

export function clearLocalDraftScope(
  conversationId: string,
  isPrivate: boolean,
  scope: DraftScope,
): void {
  try {
    sessionStorage.removeItem(makeDraftKey(conversationId, isPrivate, scope));
  } catch {
    /* ignore */
  }
}
